/**
 * Performance monitoring service for tracking rendering and performance metrics
 */

class PerformanceMonitor {
  constructor() {
    this.metrics = {
      locationRendering: [],
      clusteringPerformance: [],
      viewportLoading: [],
      cacheHits: 0,
      cacheMisses: 0
    };
    this.performanceObserver = null;
    this.enableConsoleLogging = true;
  }

  /**
   * Start timing a performance operation
   * @param {string} operation - Operation identifier
   * @returns {function} - Function to call when operation completes
   */
  startTiming(operation) {
    const startTime = performance.now();
    const startMemory = this.getMemoryUsage();
    
    return (metadata = {}) => {
      const endTime = performance.now();
      const duration = endTime - startTime;
      const endMemory = this.getMemoryUsage();
      const memoryDelta = endMemory - startMemory;
      
      const metric = {
        operation,
        duration,
        startTime,
        endTime,
        memoryDelta,
        metadata,
        timestamp: new Date().toISOString()
      };
      
      this.recordMetric(operation, metric);
      
      if (this.enableConsoleLogging) {
        console.log(`🎯 Performance: ${operation} took ${duration.toFixed(2)}ms`, metric);
      }
      
      return metric;
    };
  }

  /**
   * Record a performance metric
   * @param {string} category - Metric category
   * @param {object} metric - Metric data
   */
  recordMetric(category, metric) {
    // Initialize category if it doesn't exist
    if (!this.metrics[category]) {
      this.metrics[category] = [];
    }
    
    // Add metric to appropriate category
    this.metrics[category].push(metric);
    
    // Keep only last 100 metrics per category to prevent memory leaks
    if (this.metrics[category].length > 100) {
      this.metrics[category] = this.metrics[category].slice(-100);
    }
  }

  /**
   * Record cache hit/miss
   * @param {boolean} isHit - Whether it was a cache hit
   */
  recordCacheMetric(isHit) {
    if (isHit) {
      this.metrics.cacheHits++;
    } else {
      this.metrics.cacheMisses++;
    }
  }

  /**
   * Get cache hit ratio
   * @returns {number} - Cache hit ratio (0-1)
   */
  getCacheHitRatio() {
    const total = this.metrics.cacheHits + this.metrics.cacheMisses;
    return total > 0 ? this.metrics.cacheHits / total : 0;
  }

  /**
   * Get memory usage (if available)
   * @returns {number} - Memory usage in MB
   */
  getMemoryUsage() {
    if (window.performance && window.performance.memory) {
      return window.performance.memory.usedJSHeapSize / 1024 / 1024; // Convert to MB
    }
    return 0;
  }

  /**
   * Get performance summary for a category
   * @param {string} category - Metric category
   * @returns {object} - Performance summary
   */
  getPerformanceSummary(category) {
    const metrics = this.metrics[category] || [];
    
    if (metrics.length === 0) {
      return {
        count: 0,
        avgDuration: 0,
        minDuration: 0,
        maxDuration: 0,
        totalDuration: 0
      };
    }
    
    const durations = metrics.map(m => m.duration);
    const totalDuration = durations.reduce((sum, d) => sum + d, 0);
    
    return {
      count: metrics.length,
      avgDuration: totalDuration / metrics.length,
      minDuration: Math.min(...durations),
      maxDuration: Math.max(...durations),
      totalDuration,
      lastMetrics: metrics.slice(-5) // Last 5 metrics
    };
  }

  /**
   * Get all performance metrics
   * @returns {object} - All metrics
   */
  getAllMetrics() {
    return {
      ...this.metrics,
      cacheHitRatio: this.getCacheHitRatio(),
      memoryUsage: this.getMemoryUsage(),
      timestamp: new Date().toISOString()
    };
  }

  /**
   * Clear all metrics
   */
  clearMetrics() {
    this.metrics = {
      locationRendering: [],
      clusteringPerformance: [],
      viewportLoading: [],
      cacheHits: 0,
      cacheMisses: 0
    };
  }

  /**
   * Export metrics as JSON
   * @returns {string} - JSON string of metrics
   */
  exportMetrics() {
    return JSON.stringify(this.getAllMetrics(), null, 2);
  }

  /**
   * Log performance warning if operation is slow
   * @param {object} metric - Performance metric
   * @param {number} threshold - Warning threshold in ms
   */
  checkPerformanceWarning(metric, threshold = 1000) {
    if (metric.duration > threshold) {
      console.warn(`⚠️ Performance Warning: ${metric.operation} took ${metric.duration.toFixed(2)}ms (threshold: ${threshold}ms)`, metric);
    }
  }

  /**
   * Enable/disable console logging
   * @param {boolean} enabled - Whether to enable logging
   */
  setConsoleLogging(enabled) {
    this.enableConsoleLogging = enabled;
  }
}

// Create singleton instance
const performanceMonitor = new PerformanceMonitor();

export default performanceMonitor;